/**
 * Auth slice.
 *
 * Holds tokens and tenant slug client-side. Tokens are also persisted in
 * localStorage so a page reload doesn't log the user out.
 *
 * NOTE: storing JWTs in localStorage is a deliberate tradeoff. It's XSS-
 * exposed but works without a backend-controlled refresh cookie flow.
 * If we move to httpOnly cookies later, the slice loses the storage piece
 * and becomes a thin client cache.
 */
import { createSlice, type PayloadAction } from "@reduxjs/toolkit";

import { isAccessTokenExpired } from "@/features/auth/lib/accessToken";
import { currentAppPath, stashReturnPath } from "@/features/auth/lib/sessionReturnPath";

interface AuthUser {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  full_name: string;
  role: string;
  user_type: string;
  tenant: number | null;
}

interface AuthState {
  accessToken: string | null;
  refreshToken: string | null;
  user: AuthUser | null;
  tenantSlug: string | null;
}

const EMPTY_AUTH: AuthState = {
  accessToken: null,
  refreshToken: null,
  user: null,
  tenantSlug: null,
};

const STORAGE_KEY = "tnn.auth";

function loadFromStorage(): AuthState {
  if (typeof window === "undefined") {
    return { ...EMPTY_AUTH };
  }
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return { ...EMPTY_AUTH };
    }
    const parsed = JSON.parse(raw);
    const state: AuthState = {
      accessToken: parsed.accessToken ?? null,
      refreshToken: parsed.refreshToken ?? null,
      user: parsed.user ?? null,
      tenantSlug: parsed.tenantSlug ?? null,
    };
    if (isAccessTokenExpired(state.accessToken)) {
      stashReturnPath(currentAppPath());
      window.localStorage.removeItem(STORAGE_KEY);
      return { ...EMPTY_AUTH };
    }
    return state;
  } catch {
    return { ...EMPTY_AUTH };
  }
}

function saveToStorage(state: AuthState) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // localStorage may be full or disabled (private browsing). Non-fatal.
  }
}

const initialState: AuthState = loadFromStorage();

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    loginSucceeded(
      state,
      action: PayloadAction<{
        access: string;
        refresh: string;
        user: AuthUser;
        tenantSlug: string | null;
      }>,
    ) {
      state.accessToken = action.payload.access;
      state.refreshToken = action.payload.refresh;
      state.user = action.payload.user;
      state.tenantSlug = action.payload.tenantSlug;
      saveToStorage(state);
    },
    tokenRefreshed(state, action: PayloadAction<{ access: string }>) {
      state.accessToken = action.payload.access;
      saveToStorage(state);
    },
    logout(state) {
      state.accessToken = null;
      state.refreshToken = null;
      state.user = null;
      state.tenantSlug = null;
      saveToStorage(state);
    },
  },
});

export const { loginSucceeded, tokenRefreshed, logout } = authSlice.actions;
export const authReducer = authSlice.reducer;
export type { AuthUser, AuthState };
